import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { Category, ILookupList, Lookup } from '../models/lookup-category';
import { BaseRestService } from './base-rest.service';
import { LookupTypeService } from './lookup-type.service';
import { LookupCategoryService } from './lookup-category.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root'})
export class LookupService extends BaseRestService<Lookup, ILookupList> implements OnDestroy {

  private onLookupCategoryChanged: Subscription;
  private changed: Subscription;

  constructor(http: HttpClient, private lookupTypeService: LookupTypeService, private lookupCategoryService: LookupCategoryService, protected router: Router) {
    super(LookupService, http, 'api/lookups', 'lookup', router);

    // enable caching
    this.cacheEnabled = true;

    this.onLookupCategoryChanged = this.lookupCategoryService.onChanged.subscribe((lookupCategories) => {
      const lc = lookupCategories;
      const cache = this.getCache();
      const hydrate$ = [];
      cache.forEach((l) => {
        l.lookupCategories = lc.filter((x) => x.lookupId === l.id);
        hydrate$.push(this.hydrate(l));
      });
      if (hydrate$.length > 0){
        forkJoin(hydrate$).pipe(take(1)).subscribe();
      }
    });

    this.changed = this.onChanged.subscribe((lookups) => {
      this.lookupCategoryService.lookups = [...lookups];
    });
  }

  ngOnDestroy(){
    this.onLookupCategoryChanged?.unsubscribe();
    this.changed?.unsubscribe();
  }

  getAllByLookupTypeDescription(lookupTypeDescription: string): Observable<Lookup[]> {
    const lookupType = this.lookupTypeService.getByDescription(lookupTypeDescription);
    if (lookupType) {
      return this.getAllByLookupType(lookupType.id).pipe(take(1), map((lookups) => {
        return lookups.filter((lookup) => lookup.lookupType.description.toLowerCase() === lookupTypeDescription.toLowerCase());

      }));
    }
    return of(null);
  }

  getAllByLookupType(lookupTypeId: number): Observable<Lookup[]> {
    return this.getAllData().pipe(
      take(1),
      map((lookups) => {        
        return lookups?.length ? lookups.filter((lookup) => lookup.lookupTypeId === lookupTypeId) : [];
      })
    );
  }

  getAll() {
    this.lookupTypeService.getAllData().pipe(take(1)).subscribe((_) => {
      super.getAll();
    });
  }

  getAllDataWithLookupType(){
    return this.lookupTypeService.getAllData().pipe(take(1), switchMap((_) => {
      return super.getAllData();
    }));
  }

  getDescription(id: number): string{
    const cache = this.getCache();
    const lookup = cache.find((x) => (x.id === id));
    return (lookup) ? lookup.description : id.toString();
  }

  save(entity: Lookup, successMessage?: string) {
    if (entity){
      // make sure lookupCategory objects are not set before save...
      entity.lookupCategories.map((i) => {
        i.category = null;
        i.lookup = null;
        return i;
      });
    }
    super.save(entity, successMessage);
  }

  hydrate(item: Lookup): Observable<Lookup> {
    const hydrate$ = [];
    hydrate$.push(of(item));
    if (item.lookupCategories && (item.lookupCategories.length > 0)){
      item.lookupCategories.map((i) => {
        if ((!i.category) || (i.category.id !== i.categoryId)){
          hydrate$.push(this.lookupCategoryService.getCategoryById(i.categoryId).pipe(take(1), map((category: Category) => {
            i.category = category;
            return item;
          })));
        }
      });
    }
    if ((!item.lookupType) || (item.lookupType.id !== item.lookupTypeId)){
      hydrate$.push(this.lookupTypeService.getById(item.lookupTypeId).pipe(map((lookupType) => {
        item.lookupType = lookupType;
        return item;
      })));
    }
    if (hydrate$.length > 0){
      return forkJoin(hydrate$).pipe(take(1), map((_) => {
        item.categories = this.getCategoriesAsString(item);
        item.onlineReporting = this.checkOnlineReporting(item);
        return item;
      }));
    }
    else{
      item.categories = this.getCategoriesAsString(item);
      item.onlineReporting = this.checkOnlineReporting(item);
      return of(item);
    }
  }

  getCategoriesAsString(item: Lookup): string{
    return (item.lookupCategories) ? item.lookupCategories
      .filter((i) => i.category)
      .sort((a, b) => a.category.description.localeCompare(b.category.description))
      .map((i) => i.category.description).join(', ') : '';
  }

  checkOnlineReporting(item: Lookup): boolean{
    if (item.onlineReporting && (item.lookupCategories.length > 0)){
      return item.lookupCategories.some((i) => i.category.onlineReporting);
    }
    else{
      return item.onlineReporting;
    }
  }

  public getAllByLookupTypeDescriptionForCategory(lookupTypeDescription:string, categoryId:number=undefined):Observable<Lookup[]>{
    const lookupType = this.lookupTypeService.getByDescription(lookupTypeDescription);
    if (lookupType) {
      return this.getAllByLookupType(lookupType.id).pipe(take(1), map((lookups) => {                
          if (lookups?.length > 0) {
            var lk:Lookup[] = lookups.filter((lookup) => lookup.lookupType.description.toLowerCase() === lookupTypeDescription.toLowerCase() && lookup.hideRecord!==true);

            // tslint:disable-next-line:only-arrow-functions
            lk.sort((a, b) => a.description.localeCompare(b.description));
    
            if (categoryId==undefined || categoryId<0)
              return lk;
    
            var filteredLookup:Lookup[] = lk.filter((lookupItem) => {
              var rc = (
                !categoryId ||
                lookupItem.lookupCategories ==undefined ||
                (categoryId && 
                  (!lookupItem.lookupCategories ||
                    lookupItem.lookupCategories.length === 0 ||
                    lookupItem.lookupCategories
                      .map((i) => i.categoryId)
                      .includes(categoryId)))
              );
              return rc;
            });
            return filteredLookup;
          }
        }));        
    }
    else         
      return of([]);
  }
}
